import { CloseIcon, FilesIcon, FolderIcon, UploadIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { FormFieldError } from '@components/form';
import { Group } from '@components/layout';
import { useConfirmation } from '@components/modal';
import { ProgressBar } from '@components/progress';
import { Typography } from '@components/typography';
import { FileWithPath, getFilesFromDataTransfer } from '@helper-functions/fileSystemTraversal';
import { Stack, alpha, useTheme } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { useCallback, useRef, useState } from 'react';

export type UploadState = 'idle' | 'zipping' | 'uploading' | 'completing' | 'done' | 'error';

type SkillFileFieldProps = {
  name: string;
  uploadState: UploadState;
  uploadProgress: number;
  label?: string;
};

export const SkillFileField = ({
  name,
  uploadState,
  uploadProgress,
  label,
}: SkillFileFieldProps) => {
  const { showConfirmation } = useConfirmation();
  const { palette } = useTheme();

  const { isSubmitting } = useFormikContext();
  const [field, meta, helpers] = useField<File | File[] | null>(name);
  const [fileIsZip, , fileIsZipHelpers] = useField<boolean>(`${name}IsZip`);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const filesInputRef = useRef<HTMLInputElement>(null);

  const [dragCounter, setDragCounter] = useState(0);

  const handleFileSelect = useCallback(
    (file: File) => {
      void helpers.setValue(file);
      void fileIsZipHelpers.setValue(false);
      helpers.setError(undefined);
    },
    [helpers, fileIsZipHelpers],
  );

  // Handle folder selection (via folder picker or folder drop)
  // When appending loose files to an existing loose-file selection, merges the arrays
  const handleFolderSelect = useCallback(
    (files: FileWithPath[], append = false) => {
      if (append) {
        const merged = [...(field.value as File[]), ...files];
        void helpers.setValue(merged);
      } else {
        void helpers.setValue(files);
      }
      void fileIsZipHelpers.setValue(true);
      helpers.setError(undefined);
    },
    [field.value, helpers, fileIsZipHelpers],
  );

  // Clear selected file/folder
  const handleClearFile = useCallback(() => {
    void helpers.setValue(null);
    void fileIsZipHelpers.setValue(false);
    helpers.setError(undefined);
  }, [helpers, fileIsZipHelpers]);

  // File input change handler (.zip only)
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        helpers.setError('Please select a .zip file');
        return;
      }
      handleFileSelect(file);
    }
    // Reset the input so the same file can be re-selected
    e.target.value = '';
  };

  // Folder input change handler (webkitdirectory)
  const handleFolderInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      // Build FileWithPath array preserving relative paths from webkitdirectory
      const fileArray: FileWithPath[] = Array.from(files).map((f) => {
        const fwp = f as FileWithPath;
        fwp.fullPath = f.webkitRelativePath || f.name;
        return fwp;
      });
      handleFolderSelect(fileArray);
    }
    e.target.value = '';
  };

  // Multi-file input change handler (loose files → zip client-side)
  // Appends to existing loose-file selection if one exists (supports multiple picks)
  const handleFilesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      const fileArray: FileWithPath[] = Array.from(files).map((f) => {
        const fwp = f as FileWithPath;
        fwp.fullPath = f.name;
        return fwp;
      });
      const shouldAppend = !!fileIsZip.value && !field.value;
      handleFolderSelect(fileArray, shouldAppend);
    }
    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev - 1);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(0);

    const items = Array.from(e.dataTransfer.items || []);
    const entries = items.map((item) => item.webkitGetAsEntry?.()).filter(Boolean);
    const hasDirectory = entries.some((entry) => entry?.isDirectory);

    if (hasDirectory) {
      // Folder drop: traverse directory tree and collect all files with paths
      //   const firstDir = entries.find((entry) => entry?.isDirectory);
      const files = await getFilesFromDataTransfer(e.dataTransfer);
      if (fileIsZip.value && field.value) {
        // Replacing a .zip → confirm first
        showConfirmation({
          message: `This will replace "${(field.value as File)?.name}" with the dropped folder. Continue?`,
          affirmText: 'Replace',
          onAffirm: () => handleFolderSelect(files),
        });
      } else {
        // Empty or existing folder/files → append
        handleFolderSelect(files, !!fileIsZip.value);
      }
    } else if (items.length === 1 && e.dataTransfer.files[0]?.name.endsWith('.zip')) {
      // Single .zip file drop
      const zipFile = e.dataTransfer.files[0];
      if (field.value || fileIsZip.value) {
        // Replacing any existing selection → confirm first
        showConfirmation({
          message: 'This will replace your current selection with the dropped .zip file. Continue?',
          affirmText: 'Replace',
          onAffirm: () => handleFileSelect(zipFile),
        });
      } else {
        handleFileSelect(zipFile);
      }
    } else if (e.dataTransfer.files.length > 0) {
      // Loose files drop → will be zipped client-side
      const fileArray: FileWithPath[] = Array.from(e.dataTransfer.files).map((f) => {
        const fwp = f as FileWithPath;
        fwp.fullPath = f.name;
        return fwp;
      });
      if (field.value) {
        // Replacing a .zip → confirm first
        showConfirmation({
          message: `This will replace "${(field.value as File)?.name}" with the dropped files. Continue?`,
          affirmText: 'Replace',
          onAffirm: () => handleFolderSelect(fileArray),
        });
      } else {
        // Empty or existing folder/files → append
        handleFolderSelect(fileArray, !!fileIsZip.value);
      }
    } else {
      helpers.setError('Please drop a .zip file, folder, or files');
    }
  };

  const isDragActive = dragCounter > 0;
  const fileSize = !fileIsZip.value
    ? (field.value as File)?.size
    : field.value && Array.isArray(field.value)
      ? field.value.reduce((sum, f) => sum + f.size, 0)
      : 0;
  const fileSizeLabel =
    fileSize > 0
      ? fileSize > 1024 * 1024
        ? `${(fileSize / (1024 * 1024)).toFixed(1)} MB`
        : `${(fileSize / 1024).toFixed(1)} KB`
      : '';

  return (
    <Stack>
      <Typography
        className="deckard-text-input-label"
        component="label"
        htmlFor={name}
        mb={1}
        variant="caption2"
        color={
          isSubmitting
            ? palette.input.label.disabled
            : meta.error
              ? palette.input.label.error
              : palette.input.label.default
        }
      >
        {label || 'Skill Package'}
      </Typography>
      {/* Drop zone */}
      <Stack
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          mb: 2,
          position: 'relative',
          border: isDragActive ? '2px dashed' : '1px dashed',
          borderColor: isDragActive
            ? palette.fileUpload.border.drag
            : meta.error
              ? palette.error.main
              : palette.fileUpload.border.default,
          borderRadius: 1.5,
          padding: 3,
          minHeight: '100px',
          maxHeight:
            field.value && Array.isArray(field.value) && field.value.length > 1 ? '300px' : '150px',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isDragActive
            ? alpha(palette.primary.light, 0.2)
            : meta.error
              ? alpha(palette.error.light, 0.1)
              : palette.fileUpload.background.default,
        }}
      >
        {field.value ? (
          // Show selected file/folder info with clear button
          <Group alignItems="center" justifyContent="center" spacing={1}>
            {!fileIsZip.value ? (
              <FilesIcon size={20} color={palette.text.light} />
            ) : (
              <FolderIcon size={20} color={palette.text.light} />
            )}
            <Typography variant="body2">
              {!fileIsZip.value
                ? (field.value as File)?.name
                : `${(field.value as File[])?.length} file${(field.value as File[])?.length !== 1 ? 's' : ''} selected`}
              {fileSizeLabel && ` - ${fileSizeLabel}`}
            </Typography>
            {!isSubmitting && <IconButton size={14} onClick={handleClearFile} Icon={CloseIcon} />}
          </Group>
        ) : (
          // Show drop prompt with icon
          <Stack alignItems="center" gap={2}>
            <UploadIcon />
            <Stack alignItems="center" gap={1}>
              <Typography variant="body1">
                Drag and drop a .zip file, folder, or files here
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>

      {/* File/Folder picker buttons — show full set when nothing selected, or "Add more" when loose files exist */}
      {!isSubmitting && !field.value && (
        <Group gap={2} justifyContent="center">
          {!field.value && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{ flex: 1 }}
            >
              Choose .zip file
            </Button>
          )}
          <Button
            variant="secondary"
            size="small"
            onClick={() => filesInputRef.current?.click()}
            sx={{ flex: 1 }}
          >
            {fileIsZip.value ? 'Add more files' : 'Choose files'}
          </Button>
          {!field.value && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => folderInputRef.current?.click()}
              sx={{ flex: 1 }}
            >
              Choose folder
            </Button>
          )}
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
          <input
            ref={filesInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFilesInputChange}
          />
          <input
            ref={folderInputRef}
            type="file"
            {...({ webkitdirectory: 'true', directory: 'true' } as Record<string, string>)}
            style={{ display: 'none' }}
            onChange={handleFolderInputChange}
          />
        </Group>
      )}
      <FormFieldError error={meta.error} maintainGutters />
      {/* Upload progress indicators */}
      {uploadState === 'zipping' && (
        <Stack gap={0.5}>
          <Typography variant="caption1" color="text.light">
            Zipping {field.value && Array.isArray(field.value) ? field.value.length : ''} files...
          </Typography>
          <ProgressBar variant="determinate" value={0} />
        </Stack>
      )}
      {uploadState === 'uploading' && (
        <Stack gap={0.5}>
          <Typography variant="caption1" color="text.light">
            Uploading... {Math.round(uploadProgress * 100)}%
          </Typography>
          <ProgressBar variant="determinate" value={uploadProgress * 100} />
        </Stack>
      )}
      {uploadState === 'completing' && (
        <Stack gap={0.5}>
          <Typography variant="caption1" color="text.light">
            Finalizing...
          </Typography>
          <ProgressBar variant="determinate" value={100} />
        </Stack>
      )}
    </Stack>
  );
};
